import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, LayoutGrid, Rows3, Flame, ChevronDown } from "lucide-react";

import { ToolbarSticky } from "@/components/wyvio-ui/signature/ToolbarSticky";
import { VehicleCard } from "@/components/wyvio-ui/signature/VehicleCard";

/**
 * VehiclesMockup — Cockpit véhicules WYVIO.
 * Mission unique : "Quel véhicule mérite mon attention maintenant ?"
 *
 * Priorités :
 *   1. Priorité automatique (les cartes se trient toutes seules)
 *   2. Progression (workflow 13)
 *   3. Prochaine action
 *
 * La photo est un repère visuel (~40 %), l'information domine (~60 %).
 * Sections triées naturellement : 🔥 À traiter → En cours → À venir → Terminés.
 */

const VEHICLES = [
  // À traiter — priorité descendante
  {
    id: 1,
    name: "Porsche 911 GT3",
    plate: "AA-042-XR",
    client: "Marc Ballaud",
    silhouette: "coupe",
    completed: 9,
    priority: 98,
    priorityLabel: "Urgence élevée",
    state: "action",
    nextAction: "Signature restante",
    nextActionUrgent: true,
    orderNumber: "BC-2026-0412",
  },
  {
    id: 2,
    name: "Mercedes-Benz Classe G",
    plate: "GT-908-BM",
    client: "Familia Dupont",
    silhouette: "suv",
    completed: 4,
    priority: 92,
    priorityLabel: "Paiement en retard",
    state: "action",
    nextAction: "Relancer paiement",
    nextActionUrgent: true,
    orderNumber: "BC-2026-0414",
  },
  {
    id: 3,
    name: "Porsche Taycan Turbo",
    plate: "TC-771-EF",
    client: "Groupe Élégance",
    silhouette: "berline",
    completed: 6,
    priority: 84,
    priorityLabel: "SLA J+1",
    state: "action",
    nextAction: "Ajouter les photos",
    nextActionUrgent: false,
    orderNumber: "BC-2026-0416",
  },
  // En cours
  {
    id: 4,
    name: "Audi RS6 Performance",
    plate: "GR-091-KX",
    client: "Louis Marchand",
    silhouette: "break",
    completed: 8,
    state: "encours",
    nextAction: "Préparation en cours",
    orderNumber: "BC-2026-0413",
  },
  {
    id: 5,
    name: "Tesla Model S Plaid",
    plate: "TS-741-VL",
    client: "Antoine Vidal",
    silhouette: "berline",
    completed: 7,
    state: "encours",
    nextAction: "Transport à valider",
    orderNumber: "BC-2026-0415",
  },
  // À venir
  {
    id: 6,
    name: "Rivian R1T Launch",
    plate: "RV-330-AZ",
    client: "Studios Rivian",
    silhouette: "break",
    completed: 3,
    state: "avenir",
    nextAction: "Réception fournisseur",
    orderNumber: "BC-2026-0417",
  },
  {
    id: 7,
    name: "Range Rover Sport SVR",
    plate: "RR-508-PL",
    client: "M. Delacroix",
    silhouette: "suv",
    completed: 2,
    state: "avenir",
    nextAction: "Financement en attente",
    orderNumber: "BC-2026-0421",
  },
  // Terminés
  {
    id: 8,
    name: "BMW M4 Competition",
    plate: "BM-410-JC",
    client: "Sébastien Rossi",
    silhouette: "coupe",
    completed: 13,
    state: "termine",
    nextAction: "Livré le 22 février",
    nextActionDone: true,
    orderNumber: "BC-2026-0409",
  },
  {
    id: 9,
    name: "Range Rover Autobiography",
    plate: "RR-208-BM",
    client: "Alice Beaumont",
    silhouette: "suv",
    completed: 13,
    state: "termine",
    nextAction: "Livré le 22 février",
    nextActionDone: true,
    orderNumber: "BC-2026-0410",
  },
];

const FILTERS = [
  { key: "tous", label: "Tous" },
  { key: "prep", label: "En préparation" },
  { key: "livraison", label: "En livraison" },
  { key: "bloques", label: "Bloqués" },
  { key: "termines", label: "Livrés cette semaine" },
];

export default function VehiclesMockup() {
  const [layout, setLayout] = useState("grid"); // "grid" | "compact"
  const [filter, setFilter] = useState("tous");

  const grouped = {
    action: VEHICLES.filter((v) => v.state === "action").sort(
      (a, b) => (b.priority || 0) - (a.priority || 0),
    ),
    encours: VEHICLES.filter((v) => v.state === "encours"),
    avenir: VEHICLES.filter((v) => v.state === "avenir"),
    termine: VEHICLES.filter((v) => v.state === "termine"),
  };

  const attention = grouped.action.length;

  return (
    <>
      <Helmet>
        <title>Véhicules en suivi · wyvio</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="wyvio-signature-bg font-neue-haas min-h-screen">
        <ToolbarSticky />

        <main className="relative z-10 mx-auto max-w-[1400px] px-12 pb-24 pt-14">
          {/* Retour + tag */}
          <div className="mb-8 flex items-center justify-between">
            <Link
              to="/mockups/dashboard-typo-neue-haas"
              className="group inline-flex items-center gap-2 text-[12.5px] font-medium text-[#6B6B67] transition-colors hover:text-[#151515]"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft
                className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5"
                strokeWidth={2}
              />
              Retour au centre de pilotage
            </Link>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#6B6B67] backdrop-blur">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]" />
              Mockup · Véhicules
            </div>
          </div>

          {/* Hero éditorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-14"
          >
            <p className="text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
              Cockpit véhicules
            </p>
            <h1 className="mt-6 text-[64px] font-medium leading-[1] tracking-[-0.032em] text-[#151515]">
              {VEHICLES.length} véhicules en suivi.
            </h1>
            <p className="mt-6 text-[19px] font-normal leading-[1.4] text-[#4A4A46]">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-wyvio-elev">
                <Flame className="h-3.5 w-3.5 text-[#151515]" strokeWidth={2} />
                <span className="text-[13px] font-medium text-[#151515]">
                  {attention}
                </span>
              </span>
              <span className="ml-3">
                demandent votre attention aujourd&apos;hui.
              </span>
            </p>
          </motion.section>

          {/* Barre de contrôles : filtres + toggle layout */}
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-12 flex flex-wrap items-center justify-between gap-4"
          >
            {/* Filtres */}
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`rounded-full px-4 py-2 text-[12.5px] font-medium tracking-[-0.005em] transition-all ${
                    filter === f.key
                      ? "bg-[#151515] text-white"
                      : "bg-transparent text-[#6B6B67] hover:bg-[#F2F2EE]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Toggle layout */}
            <div className="inline-flex items-center gap-1 rounded-full bg-white p-1 shadow-[0_0_0_1px_#E8E8E2]">
              <button
                onClick={() => setLayout("grid")}
                data-testid="layout-grid"
                className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[11.5px] font-medium transition-all ${
                  layout === "grid"
                    ? "bg-[#151515] text-white"
                    : "text-[#6B6B67] hover:text-[#151515]"
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={2} />
                Grille
              </button>
              <button
                onClick={() => setLayout("compact")}
                data-testid="layout-compact"
                className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[11.5px] font-medium transition-all ${
                  layout === "compact"
                    ? "bg-[#151515] text-white"
                    : "text-[#6B6B67] hover:text-[#151515]"
                }`}
              >
                <Rows3 className="h-3.5 w-3.5" strokeWidth={2} />
                Compact
              </button>
            </div>
          </motion.section>

          {/* Sections triées naturellement */}
          <VehiclesSection
            title="À traiter"
            icon={Flame}
            count={grouped.action.length}
            vehicles={grouped.action}
            testid="section-action"
          />
          <VehiclesSection
            title="En cours"
            count={grouped.encours.length}
            vehicles={grouped.encours}
            testid="section-encours"
          />
          <VehiclesSection
            title="À venir"
            count={grouped.avenir.length}
            vehicles={grouped.avenir}
            testid="section-avenir"
          />
          <VehiclesSection
            title="Livrés"
            count={grouped.termine.length}
            vehicles={grouped.termine}
            collapsible
            defaultCollapsed
            testid="section-termine"
          />
        </main>
      </div>
    </>
  );
}

function VehiclesSection({
  title,
  icon: Icon = null,
  count,
  vehicles,
  collapsible = false,
  defaultCollapsed = false,
  testid,
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  if (vehicles.length === 0) return null;
  const Wrapper = collapsible ? "button" : "div";

  return (
    <section data-testid={testid} className="mb-16">
      <Wrapper
        {...(collapsible ? { onClick: () => setOpen((v) => !v), type: "button" } : {})}
        className="mb-6 flex w-full items-center gap-3 text-left"
      >
        {Icon && (
          <Icon className="h-3.5 w-3.5 text-[#151515]" strokeWidth={2} />
        )}
        <p className="text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#151515]">
          {title}
        </p>
        <span
          aria-hidden
          className="h-px flex-1"
          style={{ background: "#E8E8E2" }}
        />
        <span className="wyvio-tabular text-[10.5px] font-medium text-[#8A8A80]">
          {String(count).padStart(2, "0")}
        </span>
        {collapsible && (
          <ChevronDown
            className={`h-3.5 w-3.5 text-[#8A8A80] transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
            strokeWidth={2}
          />
        )}
      </Wrapper>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div className="grid grid-cols-3 gap-6">
              {vehicles.map((v, i) => (
                <VehicleCard
                  key={v.id}
                  vehicle={v}
                  index={i}
                  testid={`vehicle-${v.id}`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
